/** Role names as the backend issues them, most privileged first. */
export const ROLES = ['superadmin', 'admin', 'manager', 'user'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/** Only `admin` and `superadmin` pass `require_admin`, which gates 92 endpoints. */
export function isAdminRole(role: Role): boolean {
  return role === 'admin' || role === 'superadmin';
}

/** The user as returned by the login response — a subset of the /auth/me profile. */
export interface AuthUser {
  readonly userId: number;
  readonly username: string;
  readonly role: Role;
  readonly fullName: string | null;
  /**
   * The backend returns this but does NOT enforce it — a token with
   * must_change_password=true is fully valid on every other route. The gate is
   * entirely the frontend's responsibility.
   */
  readonly mustChangePassword: boolean;
}

/** The fuller profile from GET /auth/me. */
export interface Profile extends AuthUser {
  readonly email: string | null;
  readonly officialEmail: string | null;
  readonly isActive: boolean;
}

export interface LoginCredentials {
  /** Accepts username, email, or official_email — matched case-insensitively. */
  readonly username: string;
  readonly password: string;
}

export interface ChangePasswordInput {
  /** Always required, including the forced first change. Sending null returns 400. */
  readonly currentPassword: string;
  readonly newPassword: string;
}

export interface ResetPasswordInput {
  readonly token: string;
  readonly newPassword: string;
}

/** Minimum the backend accepts; shorter values are rejected with 422. */
export const MIN_PASSWORD_LENGTH = 8;
